import { useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import ProjectNav from "../components/ProjectNav";

export default function Configure() {
  const { projectId } = useParams();
  const { state } = useLocation() as any;
  const navigate = useNavigate();
  const name = localStorage.getItem("name") || "User";
  const initials = name.split(" ").map((n: string) => n[0]).join("").toUpperCase();

  // EVM ↔ Staffing mapping
  const [evmStaffingLeft, setEvmStaffingLeft] = useState("WBS/CA/WP");
  const [evmStaffingRight, setEvmStaffingRight] = useState("WBS_Code");

  // EVM ↔ Jira mapping
  const [evmJiraLeft, setEvmJiraLeft] = useState("NWA");
  const [evmJiraRight, setEvmJiraRight] = useState("Epic_Link / WBS_Tag");

  // Extra mappings added manually
  const [extraMappings, setExtraMappings] = useState<any[]>([]);


  const evmColumns = ["WBS/CA/WP", "NWA", "WBS", "Control Account", "Work Package", "OBS"];
  const staffingColumns = ["WBS_Code", "Target Object", "Work_Center", "Emp_Name", "NWA"];
  const jiraColumns = ["Epic_Link / WBS_Tag", "Epic Link", "WBS_Tag", "Custom_Field_WBS", "Component/s"];

  const steps = ["Upload files", "Run Sanity Checks", "Configure", "Review & generate"];

  const addMapping = () => {
    setExtraMappings(m => [...m, { id: Date.now(), leftSource: "M170 EVM", rightSource: "Staffing", leftCol: "WBS/CA/WP", rightCol: "WBS_Code" }]);
  };

  const removeMapping = (id: number) => {
    setExtraMappings(m => m.filter(x => x.id !== id));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ProjectNav projectId={projectId!} active="report" />

      <div className="max-w-5xl mx-auto px-6 py-8">

        {/* Header */}
        <div className="flex items-start justify-between mb-2">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">CONFIGURE REPORT</h1>
            <p className="text-xs text-gray-400 mt-0.5 uppercase tracking-wide">
              Review and adjust where needed — answers will be saved and can be reused as a template for future quarters.
            </p>
          </div>
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input placeholder="Search programs by name or code..."
              className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none w-64" />
          </div>
        </div>

        {/* Steps */}
        <div className="flex items-center gap-0 mb-8 mt-6">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center">
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium
                  ${i < 2 ? "bg-green-500 text-white"
                    : i === 2 ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-500"}`}>
                  {i < 2 ? "✓" : i + 1}
                </div>
                <span className={`text-sm
                  ${i < 2 ? "text-green-600"
                    : i === 2 ? "text-blue-600 font-medium"
                    : "text-gray-400"}`}>{s}</span>
              </div>
              {i < steps.length - 1 && <div className="w-8 h-px bg-gray-300 mx-3"></div>}
            </div>
          ))}
        </div>

        {/* ── File Column Mapping section ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-5">
            <p className="text-sm font-semibold text-gray-900">File Column Mapping</p>
            <button
              onClick={addMapping}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors">
              + Add Manually
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">

            {/* ── EVM ↔ Staffing mapping card ── */}
            <div className="border border-gray-200 rounded-xl p-4">
              {/* Source selectors row */}
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs text-gray-500 font-medium">EVM</span>
                <span className="text-xs text-gray-400">Staffing</span>
                <select
                  value="M170 EVM"
                  className="flex-1 border border-blue-200 bg-blue-50 text-blue-700 rounded-lg px-2 py-1 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option>M170 EVM</option>
                </select>
                {/* Link icon */}
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                <select
                  value="Staffing"
                  className="flex-1 border border-pink-200 bg-pink-50 text-pink-600 rounded-lg px-2 py-1 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-pink-400"
                >
                  <option>Staffing</option>
                </select>
                <button className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 border border-red-200 rounded-lg px-2 py-1 hover:bg-red-50 transition-colors">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Remove
                </button>
              </div>

              {/* Column selectors */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <p className="text-xs text-gray-400 mb-1.5">Column in EVM file</p>
                  <select
                    value={evmStaffingLeft}
                    onChange={e => setEvmStaffingLeft(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    {evmColumns.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1.5">Column in Staffing file</p>
                  <select
                    value={evmStaffingRight}
                    onChange={e => setEvmStaffingRight(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    {staffingColumns.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              {/* Match indicator */}
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-gray-400">
                  EVM | <span className="text-gray-600">{evmStaffingLeft}</span> ↔ Staffing | <span className="text-gray-600">{evmStaffingRight}</span>
                </p>
                <span className="text-xs font-semibold text-green-600">
                  {state?.sanityResult?.cross_file?.evm_staffing_match_pct
                    ? `${state.sanityResult.cross_file.evm_staffing_match_pct}% Match`
                    : "97% Match"}
                </span>
              </div>

              {/* Info box */}
              <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5">
                <div className="flex items-start gap-2">
                  <svg className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-xs text-blue-700 leading-relaxed">
                    {state?.sanityResult?.cross_file?.evm_staffing_info
                      ? state.sanityResult.cross_file.evm_staffing_info
                      : "Auto-detected: EVM records matched in Staffing. Unmatched control accounts will appear as 'no staffing data' in the report."}
                  </p>
                </div>
              </div>
            </div>

            {/* ── EVM ↔ Jira mapping card ── */}
            <div className="border border-gray-200 rounded-xl p-4">
              {/* Source selectors row */}
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs text-gray-500 font-medium">EVM</span>
                <span className="text-xs text-gray-400">Staffing</span>
                <select
                  value="M170 EVM"
                  className="flex-1 border border-blue-200 bg-blue-50 text-blue-700 rounded-lg px-2 py-1 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option>M170 EVM</option>
                </select>
                {/* Link icon */}
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                <select
                  value="Jira data"
                  className="flex-1 border border-orange-200 bg-orange-50 text-orange-600 rounded-lg px-2 py-1 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-orange-400"
                >
                  <option>Jira data</option>
                </select>
                <button className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 border border-red-200 rounded-lg px-2 py-1 hover:bg-red-50 transition-colors">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Remove
                </button>
              </div>

              {/* Column selectors */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <p className="text-xs text-gray-400 mb-1.5">Column in EVM file</p>
                  <select
                    value={evmJiraLeft}
                    onChange={e => setEvmJiraLeft(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    {evmColumns.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1.5">Column in Jira file</p>
                  <select
                    value={evmJiraRight}
                    onChange={e => setEvmJiraRight(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    {jiraColumns.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              {/* Match indicator */}
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-gray-400">
                  EVM | <span className="text-gray-600">{evmJiraLeft}</span> ↔ Jira | <span className="text-gray-600">{evmJiraRight}</span>
                </p>
                <span className="text-xs font-semibold text-amber-600">
                  {state?.sanityResult?.cross_file?.evm_jira_match_pct
                    ? `${state.sanityResult.cross_file.evm_jira_match_pct}% Match`
                    : "67% Match"}
                </span>
              </div>

              {/* Warning info box */}
              <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5">
                <div className="flex items-start gap-2">
                  <svg className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    {state?.sanityResult?.cross_file?.evm_jira_info
                      ? state.sanityResult.cross_file.evm_jira_info
                      : "Some EVM NWA codes could not find a matching Jira epic tag. Unmatched work packages will show schedule data as 'N/A'. Consider using Custom_Field_WBS if your Jira project uses a dedicated WBS custom field."}
                  </p>
                </div>
              </div>
            </div>

            {/* ── Extra manually added mappings ── */}
            {extraMappings.map((m) => (
              <div key={m.id} className="border border-dashed border-gray-300 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-4">
                  <select className="flex-1 border border-blue-200 bg-blue-50 text-blue-700 rounded-lg px-2 py-1 text-xs font-medium focus:outline-none">
                    <option>M170 EVM</option>
                  </select>
                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  <select className="flex-1 border border-pink-200 bg-pink-50 text-pink-600 rounded-lg px-2 py-1 text-xs font-medium focus:outline-none">
                    <option>Staffing</option>
                    <option>Jira data</option>
                  </select>
                  <button onClick={() => removeMapping(m.id)}
                    className="flex items-center gap-1 text-xs text-red-500 border border-red-200 rounded-lg px-2 py-1 hover:bg-red-50">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Remove
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-400 mb-1.5">Column in EVM file</p>
                    <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                      {evmColumns.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-1.5">Column in target file</p>
                    <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                      {staffingColumns.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom buttons */}
        <div className="flex justify-between mt-5">
          <div className="flex gap-2">
            <button onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
              ← Back
            </button>
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
              </svg>
              Save as template
            </button>
          </div>
          <button
            onClick={() => navigate(`/review/${projectId}`, { 
              state: {
                ...state,
                form: {
                  ...state?.form,
                  quarter: state?.month,
                  data_as_of: state?.dataAsOf,
                  report_title: state?.reportTitle,
                  variance_threshold: "500000",
                  cpi_red: "0.90",
                  cpi_amber: "0.95",
                  eac_method: "Recalculate: ACWP + ETC",
                  comparison_baseline: "Prior quarter"
                }
              }
            })}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors">
            Review →
          </button>
        </div>

      </div>
    </div>
  );
}
